/**
 * BlogManager — the admin "Press" console.
 *
 *   • Compose / edit posts with a markdown body + live glam preview
 *   • Upload a cover image and inline photos — every upload is auto-watermarked
 *     server-side (AB8 BEAUTY) before it ever hits the public uploads dir
 *   • Collapsible list of all posts (draft + published) with edit / delete
 *
 * Talks to /api/blog (+ /api/blog/admin/*). Mounted at /admin/press.
 */
import { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import BlogContent from '../storefront/BlogContent';
import { Icon } from './AdminIcons';

const EMPTY = {
  title: '', slug: '', excerpt: '', body: '', coverImage: '', coverImageAlt: '',
  category: '', tags: '', author: '', status: 'draft', featured: false,
};

const fmtDate = (d) => { try { return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return ''; } };

function StatusPill({ status }) {
  const live = status === 'published';
  return (
    <span className={'inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 ' +
      (live ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700')}>
      <span className={'w-1.5 h-1.5 rounded-full ' + (live ? 'bg-emerald-500' : 'bg-amber-500')} />
      {live ? 'Published' : 'Draft'}
    </span>
  );
}

export default function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // form object when composing/editing, else null
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingInline, setUploadingInline] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const bodyRef = useRef(null);
  const coverInputRef = useRef(null);
  const inlineInputRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get('/blog/admin/list')
      .then(res => setPosts(res.data || []))
      .catch(() => toast.error('Failed to load posts'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const startNew = () => {
    setForm(EMPTY);
    setEditing({ id: null });
    setShowPreview(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const startEdit = async (post) => {
    try {
      const { data } = await api.get(`/blog/admin/${post.id}`);
      setForm({
        title: data.title || '', slug: data.slug || '', excerpt: data.excerpt || '',
        body: data.body || '', coverImage: data.coverImage || '', coverImageAlt: data.coverImageAlt || '',
        category: data.category || '', tags: (data.tags || []).join(', '), author: data.author || '',
        status: data.status || 'draft', featured: !!data.featured,
      });
      setEditing({ id: data.id });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      toast.error('Could not open that post');
    }
  };

  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async (publishOverride) => {
    if (!form.title.trim()) { toast.error('Give your post a title'); return; }
    setSaving(true);
    const payload = { ...form };
    if (publishOverride) payload.status = publishOverride;
    try {
      if (editing.id) {
        await api.put(`/blog/${editing.id}`, payload);
        toast.success('Post updated');
      } else {
        const { data } = await api.post('/blog', payload);
        setEditing({ id: data.id });
        toast.success('Post created');
      }
      if (publishOverride) setField('status', publishOverride);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This removes it from the Press section permanently.`)) return;
    try {
      await api.delete(`/blog/${post.id}`);
      setPosts(prev => prev.filter(p => p.id !== post.id));
      if (editing?.id === post.id) cancel();
      toast.success('Post deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  // Upload helper - returns the watermarked URL
  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append('image', file);
    const { data } = await api.post('/blog/upload', fd);
    return data.url;
  };

  const onCoverFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    try {
      const url = await uploadImage(file);
      setField('coverImage', url);
      toast.success('Cover uploaded & watermarked');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploadingCover(false);
      if (coverInputRef.current) coverInputRef.current.value = '';
    }
  };

  // Insert text at the textarea caret (so inline images land where you're typing).
  const insertAtCaret = (snippet) => {
    const el = bodyRef.current;
    const cur = form.body || '';
    if (!el) { setField('body', cur + snippet); return; }
    const start = el.selectionStart ?? cur.length;
    const end = el.selectionEnd ?? cur.length;
    const next = cur.slice(0, start) + snippet + cur.slice(end);
    setField('body', next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + snippet.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const onInlineFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingInline(true);
    try {
      const url = await uploadImage(file);
      insertAtCaret(`\n\n![${file.name.replace(/\.[^.]+$/, '')}](${url})\n\n`);
      toast.success('Photo inserted & watermarked');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploadingInline(false);
      if (inlineInputRef.current) inlineInputRef.current.value = '';
    }
  };

  const TOOLBAR = [
    { label: 'H2', snip: '\n\n## Heading\n\n', title: 'Section heading' },
    { label: 'H3', snip: '\n\n### Subheading\n\n', title: 'Subheading' },
    { label: 'B', snip: '**bold**', title: 'Bold', cls: 'font-bold' },
    { label: 'i', snip: '*italic*', title: 'Italic', cls: 'italic' },
    { label: 'Quote', snip: '\n\n> Your pull-quote here\n\n', title: 'Pull-quote' },
    { label: 'List', snip: '\n\n- First\n- Second\n- Third\n\n', title: 'Bullet list' },
    { label: 'Divider', snip: '\n\n---\n\n', title: 'Polka-dot divider' },
    { label: 'Link', snip: '[link text](https://)', title: 'Link' },
  ];

  const inputCls = 'w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-tea-500';
  const labelCls = 'block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1';

  return (
    <div>
      {/* ── Header ───────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2"><Icon name="press" className="w-6 h-6 text-tea-500" /> Press &amp; Blog</h2>
          <p className="text-sm text-gray-500 mt-1">
            Author the glam stories shown on the public <span className="font-medium">/press</span> feed. Photos are auto-watermarked on upload.
          </p>
        </div>
        {!editing && (
          <button onClick={startNew}
            className="inline-flex items-center gap-2 rounded-full bg-tea-600 hover:bg-tea-700 text-white text-sm font-semibold px-5 py-2.5 shadow-sm">
            <Icon name="sparkles" className="w-4 h-4" /> New Post
          </button>
        )}
      </div>

      {/* ── Editor ───────────────────────────────────────────────────────────── */}
      {editing && (
        <div className="rounded-2xl border border-tea-100 bg-white shadow-sm mb-10 overflow-hidden">
          <div className="px-5 py-3 polka-pink border-b border-tea-100 flex items-center justify-between">
            <span className="font-bold text-gray-800">{editing.id ? 'Edit post' : 'New post'}</span>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowPreview(p => !p)}
                className="text-xs font-medium text-tea-700 bg-white/70 rounded-full px-3 py-1.5 ring-1 ring-tea-100 hover:bg-white">
                {showPreview ? 'Hide preview' : 'Show preview'}
              </button>
              <button onClick={cancel} className="text-gray-400 hover:text-gray-700 text-xl leading-none px-1">×</button>
            </div>
          </div>

          <div className={'grid gap-0 ' + (showPreview ? 'lg:grid-cols-2' : 'grid-cols-1')}>
            {/* form column */}
            <div className="p-5 space-y-4 border-r border-gray-100">
              <div>
                <label className={labelCls}>Title</label>
                <input className={inputCls} value={form.title} maxLength={220}
                  onChange={e => setField('title', e.target.value)} placeholder="e.g. The 5-Minute Morning Glow Ritual" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Category</label>
                  <input className={inputCls} value={form.category} maxLength={80}
                    onChange={e => setField('category', e.target.value)} placeholder="Rituals" />
                </div>
                <div>
                  <label className={labelCls}>Author</label>
                  <input className={inputCls} value={form.author} maxLength={120}
                    onChange={e => setField('author', e.target.value)} placeholder="The AB8 Edit" />
                </div>
              </div>

              <div>
                <label className={labelCls}>Tags (comma-separated)</label>
                <input className={inputCls} value={form.tags} maxLength={500}
                  onChange={e => setField('tags', e.target.value)} placeholder="glow, skincare, GHK-Cu" />
              </div>

              <div>
                <label className={labelCls}>Excerpt (shown on cards)</label>
                <textarea className={inputCls + ' resize-none'} rows={2} value={form.excerpt} maxLength={500}
                  onChange={e => setField('excerpt', e.target.value)} placeholder="A one or two line teaser…" />
              </div>

              {/* Cover image */}
              <div>
                <label className={labelCls}>Cover image (auto-watermarked)</label>
                {form.coverImage ? (
                  <div className="relative rounded-xl overflow-hidden ring-1 ring-tea-100 group">
                    <img src={form.coverImage} alt="cover" className="w-full h-40 object-cover" />
                    <button onClick={() => setField('coverImage', '')}
                      className="absolute top-2 right-2 bg-black/60 text-white text-xs rounded-full px-2.5 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      Remove
                    </button>
                  </div>
                ) : (
                  <button onClick={() => coverInputRef.current?.click()} disabled={uploadingCover}
                    className="w-full border-2 border-dashed border-tea-200 rounded-xl py-8 text-sm text-tea-600 hover:bg-tea-50 disabled:opacity-50">
                    {uploadingCover ? 'Watermarking…' : (
                      <span className="inline-flex items-center gap-1.5"><Icon name="media" className="w-4 h-4" /> Upload cover photo</span>
                    )}
                  </button>
                )}
                <input ref={coverInputRef} type="file" accept="image/*" onChange={onCoverFile} className="hidden" />
                {form.coverImage && (
                  <input className={inputCls + ' mt-2'} value={form.coverImageAlt} maxLength={255}
                    onChange={e => setField('coverImageAlt', e.target.value)} placeholder="Cover alt text (accessibility)" />
                )}
              </div>

              {/* Body + toolbar */}
              <div>
                <label className={labelCls}>Story (markdown)</label>
                <div className="flex flex-wrap gap-1 mb-2">
                  {TOOLBAR.map(t => (
                    <button key={t.label} title={t.title} onClick={() => insertAtCaret(t.snip)}
                      className={'text-xs rounded-md border border-gray-200 bg-gray-50 hover:bg-tea-50 hover:border-tea-200 px-2 py-1 text-gray-600 ' + (t.cls || '')}>
                      {t.label}
                    </button>
                  ))}
                  <button title="Insert photo" onClick={() => inlineInputRef.current?.click()} disabled={uploadingInline}
                    className="text-xs rounded-md border border-tea-200 bg-tea-50 hover:bg-tea-100 px-2 py-1 text-tea-700 font-medium disabled:opacity-50">
                    {uploadingInline ? 'Watermarking…' : (
                      <span className="inline-flex items-center gap-1.5"><Icon name="media" className="w-3.5 h-3.5" /> Photo</span>
                    )}
                  </button>
                  <input ref={inlineInputRef} type="file" accept="image/*" onChange={onInlineFile} className="hidden" />
                </div>
                <textarea ref={bodyRef} className={inputCls + ' font-mono text-[13px] leading-relaxed'} rows={16}
                  value={form.body} onChange={e => setField('body', e.target.value)}
                  placeholder={'Write your story…\n\n## A heading\n\nA paragraph with **bold** and *italic*.\n\n> A glam pull-quote.\n\n- a list item'} />
                <p className="text-[11px] text-gray-400 mt-1">
                  ~{Math.max(1, Math.round((form.body.split(/\s+/).filter(Boolean).length) / 200))} min read · Supports # headings, **bold**, *italic*, &gt; quotes, lists, --- dividers, and photos.
                </p>
              </div>

              {/* Status row */}
              <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t">
                <label className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={form.featured} onChange={e => setField('featured', e.target.checked)}
                    className="rounded text-tea-600 focus:ring-tea-500" />
                  <Icon name="sparkles" className="w-4 h-4 text-tea-500" /> Feature at top of Press
                </label>
                <span className="text-xs text-gray-400">Status: <StatusPill status={form.status} /></span>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => save('draft')} disabled={saving}
                  className="rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold px-4 py-2 hover:bg-gray-50 disabled:opacity-50">
                  {saving ? 'Saving…' : 'Save draft'}
                </button>
                <button onClick={() => save('published')} disabled={saving}
                  className="rounded-lg bg-tea-600 hover:bg-tea-700 text-white text-sm font-semibold px-5 py-2 disabled:opacity-50">
                  {saving ? 'Saving…' : (form.status === 'published' ? 'Update & keep live' : (
                    <span className="inline-flex items-center gap-1.5"><Icon name="rocket" className="w-4 h-4" /> Publish</span>
                  ))}
                </button>
                {editing.id && form.status === 'published' && (
                  <button onClick={() => save('draft')} disabled={saving}
                    className="text-xs text-amber-600 hover:text-amber-800 underline">Unpublish</button>
                )}
                <button onClick={cancel} className="text-xs text-gray-400 hover:text-gray-600 ml-auto">Close editor</button>
              </div>
            </div>

            {/* preview column */}
            {showPreview && (
              <div className="bg-gray-50/60 p-5 overflow-y-auto" style={{ maxHeight: '82vh' }}>
                <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 mb-3 text-center">Live preview</div>
                <div className="bg-white rounded-2xl ring-1 ring-gray-100 overflow-hidden">
                  {form.coverImage && <img src={form.coverImage} alt="" className="w-full h-44 object-cover" />}
                  <div className="p-6">
                    {form.category && (
                      <span className="inline-block rounded-full bg-tea-600 text-white text-[9px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 mb-3">{form.category}</span>
                    )}
                    <h1 className="font-display text-3xl font-extrabold text-gray-900 leading-tight">{form.title || 'Untitled story'}</h1>
                    {form.excerpt && <p className="mt-3 font-display text-lg italic text-gray-500">{form.excerpt}</p>}
                    <div className="flex items-center justify-center gap-2 my-6" aria-hidden="true">
                      <span className="w-2 h-2 rounded-full bg-tea-400" /><span className="w-2 h-2 rounded-full bg-tea-300" /><span className="w-2 h-2 rounded-full bg-tea-400" />
                    </div>
                    <BlogContent body={form.body} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Collapsible post list ────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-gray-700">All posts <span className="text-gray-400 font-normal">({posts.length})</span></h3>
      </div>

      {loading && <div className="py-12 text-center text-gray-400">Loading…</div>}

      {!loading && posts.length === 0 && (
        <div className="py-16 text-center bg-white rounded-2xl border">
          <div className="flex justify-center mb-3 text-tea-300"><Icon name="press" className="w-12 h-12" /></div>
          <p className="text-gray-500 text-sm">No posts yet — hit <span className="font-semibold">New Post</span> to write the first one.</p>
        </div>
      )}

      {!loading && posts.length > 0 && (
        <div className="bg-white border rounded-2xl divide-y overflow-hidden">
          {posts.map(post => {
            const open = expandedId === post.id;
            return (
              <div key={post.id}>
                {/* Row header (click to collapse/expand the action menu) */}
                <button onClick={() => setExpandedId(open ? null : post.id)}
                  className="w-full flex items-center gap-4 p-4 text-left hover:bg-gray-50 transition-colors">
                  <span className="inline-flex h-12 w-12 shrink-0 rounded-lg overflow-hidden ring-1 ring-gray-100 bg-gradient-to-br from-pink-100 to-fuchsia-100 items-center justify-center">
                    {post.coverImage
                      ? <img src={post.coverImage} alt="" className="h-full w-full object-cover" />
                      : <Icon name="media" className="w-5 h-5 text-tea-300" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 truncate">{post.title}</span>
                      {post.featured && <span title="Featured" className="text-tea-500 inline-flex"><Icon name="sparkles" className="w-3.5 h-3.5" /></span>}
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-gray-400 mt-0.5 flex-wrap">
                      <StatusPill status={post.status} />
                      {post.category && <span>· {post.category}</span>}
                      <span>· {post.publishedAt ? fmtDate(post.publishedAt) : fmtDate(post.createdAt)}</span>
                      <span className="inline-flex items-center gap-1">· <Icon name="views" className="w-3.5 h-3.5" /> {post.viewCount || 0}</span>
                      <span>· {post.readMinutes || 1} min</span>
                    </div>
                  </div>
                  <svg className={'w-5 h-5 text-gray-400 transition-transform shrink-0 ' + (open ? 'rotate-180' : '')}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Collapsible action menu */}
                {open && (
                  <div className="px-4 pb-4 -mt-1">
                    <div className="rounded-xl bg-tea-50/60 ring-1 ring-tea-100 p-4 flex flex-wrap items-center gap-2">
                      {post.excerpt && <p className="text-sm text-gray-500 w-full mb-1 line-clamp-2">{post.excerpt}</p>}
                      <button onClick={() => startEdit(post)}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-tea-600 hover:bg-tea-700 text-white text-sm font-semibold px-4 py-2">
                        Edit
                      </button>
                      {post.status === 'published' && (
                        <a href={`/press/${post.slug}`} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-lg border border-tea-200 text-tea-700 text-sm font-medium px-4 py-2 hover:bg-white">
                          View live
                        </a>
                      )}
                      <button onClick={() => remove(post)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 text-rose-600 text-sm font-medium px-4 py-2 hover:bg-rose-50 ml-auto">
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
